import React, { useState, useCallback, useEffect } from "react";
import {
  Card,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import QuickQuizService, {
  SectionInput,
  CreateQuizPayload,
  SearchedQuestion,
  LookupData,
  InlineQuestionPayload,
} from "../../_services/QuickQuizService";
import { localInputToUtc, utcToLocalInput } from "../../_helpers/datetime";

// ========================================
// Types
// ========================================

interface InlineQuestion {
  tempId: string;
  questionText: string;
  questionType: "mcq" | "tf" | "descriptive";
  options: string[];
  correctAnswer: string; // option index ("0","1",...) or text for descriptive
  marks: number;
  difficulty: string;
  concept: string;
}

interface SectionState {
  name: string;
  questionIds: string; // comma-separated string from textarea
  source: "question_bank" | "test_section";
  keywordId: string;
  subSections: SubSectionState[];
  inlineQuestions: InlineQuestion[];
}

interface SubSectionState {
  name: string;
  questionIds: string;
  source: "question_bank" | "test_section";
}

interface QuizFormPanelProps {
  lookups?: LookupData | null;
  editingTestId?: number | null;
  initialData?: {
    testName: string;
    testDate: string;
    testTime: string;
    categoryId: string;
    testTypeId: string;
    playerId: string;
    testResultTypeId: string;
    startDate?: string;
    endDate?: string;
    testCutOffTime?: string;
    yearIds?: number[];
    instructions?: string;
    mobileInstructions?: string;
    testDeclarationRemarks?: string;
    studentAnnouncementRemarks?: string;
    sections: SectionState[];
  };
  onSuccess?: (result: {
    testId: number;
    totalQuestions: number;
    totalMarks: number;
  }) => void;
  onCancelEdit?: () => void;
  /** When provided, called after successful create with testId+name. Used for embedding (e.g. LM Builder modal). */
  onCreated?: (result: { testId: number; name: string }) => void;
}

// ========================================
// Constants & Helpers
// ========================================

const MAX_SECTIONS = 5;
const MAX_SUB_SECTIONS = 5;

const emptySubSection = (): SubSectionState => ({
  name: "",
  questionIds: "",
  source: "question_bank",
});

const emptySection = (): SectionState => ({
  name: "",
  questionIds: "",
  source: "question_bank",
  keywordId: "",
  subSections: [],
  inlineQuestions: [],
});

let inlineIdCounter = 0;
const newInlineQuestion = (): InlineQuestion => ({
  tempId: `iq-${++inlineIdCounter}`,
  questionText: "",
  questionType: "mcq",
  options: ["", ""],
  correctAnswer: "",
  marks: 1,
  difficulty: "Easy",
  concept: "",
});

const parseIds = (str: string): number[] => {
  return str
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "")
    .map(Number)
    .filter((n) => !isNaN(n) && n > 0);
};

// ========================================
// Component
// ========================================

const QuizFormPanel: React.FC<QuizFormPanelProps> = ({
  lookups: lookupsProp,
  editingTestId,
  initialData,
  onSuccess,
  onCancelEdit,
  onCreated,
}) => {
  // Self-loading lookups when not provided by parent (e.g. when used in a modal)
  const [selfLookups, setSelfLookups] = useState<LookupData | null>(null);
  useEffect(() => {
    if (!lookupsProp) {
      QuickQuizService.getLookups()
        .then(setSelfLookups)
        .catch((err) => console.error("Failed to load lookups:", err));
    }
  }, [lookupsProp]);
  const lookups = lookupsProp ?? selfLookups;

  // Form state
  const [testName, setTestName] = useState(initialData?.testName ?? "");
  const [testDate, setTestDate] = useState(initialData?.testDate ?? "");
  const [testTime, setTestTime] = useState(initialData?.testTime ?? "");
  const [categoryId, setCategoryId] = useState(initialData?.categoryId ?? "");
  const [testTypeId, setTestTypeId] = useState(initialData?.testTypeId ?? "");
  const [playerId, setPlayerId] = useState(initialData?.playerId ?? "");
  const [testResultTypeId, setTestResultTypeId] = useState(initialData?.testResultTypeId ?? "");
  const [startDate, setStartDate] = useState(utcToLocalInput(initialData?.startDate));
  const [endDate, setEndDate] = useState(utcToLocalInput(initialData?.endDate));
  const [testCutOffTime, setTestCutOffTime] = useState(utcToLocalInput(initialData?.testCutOffTime));
  const [sortOrder, setSortOrder] = useState("");
  const [choiceSequence, setChoiceSequence] = useState("");
  const [testPassword, setTestPassword] = useState("");
  const [remarks, setRemarks] = useState("");
  const [instructions, setInstructions] = useState(initialData?.instructions ?? "");
  const [mobileInstructions, setMobileInstructions] = useState(initialData?.mobileInstructions ?? "");
  const [testDeclarationRemarks, setTestDeclarationRemarks] = useState(initialData?.testDeclarationRemarks ?? "");
  const [studentAnnouncementRemarks, setStudentAnnouncementRemarks] = useState(initialData?.studentAnnouncementRemarks ?? "");
  const [rankDefinition, setRankDefinition] = useState(false);
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>(initialData?.yearIds ?? []);
  const [hasQuestions, setHasQuestions] = useState(() => {
    if (!initialData?.sections) return true;
    // If editing a test with no real questions, default to unchecked
    return initialData.sections.some(
      (s) => s.questionIds.trim() !== "" || s.inlineQuestions.length > 0 ||
        s.subSections.some((sub) => sub.questionIds.trim() !== "")
    );
  });
  const [sections, setSections] = useState<SectionState[]>(
    initialData?.sections ?? [emptySection()]
  );

  // Auto-select when a dropdown has exactly one option
  useEffect(() => {
    if (!lookups || initialData) return;
    if (lookups.categories.length === 1 && !categoryId) setCategoryId(String(lookups.categories[0].id));
    if (lookups.testTypes.length === 1 && !testTypeId) setTestTypeId(String(lookups.testTypes[0].id));
    if (lookups.players.length === 1 && !playerId) setPlayerId(String(lookups.players[0].id));
  }, [lookups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSource, setSearchSource] = useState<
    "question_bank" | "test_section" | "both"
  >("both");
  const [searchResults, setSearchResults] = useState<SearchedQuestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [targetSectionIndex, setTargetSectionIndex] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Section management
  const addSection = () => {
    if (sections.length < MAX_SECTIONS) {
      setSections([...sections, emptySection()]);
    }
  };

  const removeSection = (index: number) => {
    if (sections.length <= 1) {
      // Last section — turn off the questions toggle and reset
      setHasQuestions(false);
      setSections([emptySection()]);
      setTargetSectionIndex(0);
      return;
    }
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
    // Clamp target section index if it's now out of bounds
    if (targetSectionIndex >= newSections.length) {
      setTargetSectionIndex(Math.max(0, newSections.length - 1));
    }
  };

  const updateSection = (
    index: number,
    field: keyof SectionState,
    value: any
  ) => {
    const updated = [...sections];
    (updated[index] as any)[field] = value;
    setSections(updated);
  };

  const addSubSection = (sectionIndex: number) => {
    const updated = [...sections];
    if (updated[sectionIndex].subSections.length < MAX_SUB_SECTIONS) {
      updated[sectionIndex].subSections.push(emptySubSection());
      setSections(updated);
    }
  };

  const removeSubSection = (sectionIndex: number, subIndex: number) => {
    const updated = [...sections];
    updated[sectionIndex].subSections = updated[
      sectionIndex
    ].subSections.filter((_, i) => i !== subIndex);
    setSections(updated);
  };

  const updateSubSection = (
    sectionIndex: number,
    subIndex: number,
    field: keyof SubSectionState,
    value: any
  ) => {
    const updated = [...sections];
    (updated[sectionIndex].subSections[subIndex] as any)[field] = value;
    setSections(updated);
  };

  // Inline question management
  const addInlineQuestion = (sectionIndex: number) => {
    const updated = [...sections];
    updated[sectionIndex].inlineQuestions.push(newInlineQuestion());
    setSections(updated);
  };

  const removeInlineQuestion = (sectionIndex: number, iqIndex: number) => {
    const updated = [...sections];
    updated[sectionIndex].inlineQuestions = updated[
      sectionIndex
    ].inlineQuestions.filter((_, i) => i !== iqIndex);
    setSections(updated);
  };

  const updateInlineQuestion = (
    sectionIndex: number,
    iqIndex: number,
    changes: Partial<InlineQuestion>
  ) => {
    const updated = [...sections];
    const iq = { ...updated[sectionIndex].inlineQuestions[iqIndex], ...changes };

    // When switching question type, adjust options
    if (changes.questionType) {
      if (changes.questionType === "tf") {
        iq.options = ["True", "False"];
        iq.correctAnswer = "";
      } else if (changes.questionType === "descriptive") {
        iq.options = [];
        iq.correctAnswer = "";
      } else if (changes.questionType === "mcq" && iq.options.length < 2) {
        iq.options = ["", ""];
        iq.correctAnswer = "";
      }
    }

    updated[sectionIndex].inlineQuestions[iqIndex] = iq;
    setSections(updated);
  };

  const updateInlineOption = (
    sectionIndex: number,
    iqIndex: number,
    optIndex: number,
    value: string
  ) => {
    const updated = [...sections];
    const opts = [...updated[sectionIndex].inlineQuestions[iqIndex].options];
    opts[optIndex] = value;
    updated[sectionIndex].inlineQuestions[iqIndex].options = opts;
    setSections(updated);
  };

  const addInlineOption = (sectionIndex: number, iqIndex: number) => {
    const updated = [...sections];
    const iq = updated[sectionIndex].inlineQuestions[iqIndex];
    if (iq.options.length < 5) {
      iq.options.push("");
      setSections(updated);
    }
  };

  const removeInlineOption = (
    sectionIndex: number,
    iqIndex: number,
    optIndex: number
  ) => {
    const updated = [...sections];
    const iq = updated[sectionIndex].inlineQuestions[iqIndex];
    if (iq.options.length > 2) {
      iq.options = iq.options.filter((_, i) => i !== optIndex);
      // Adjust correct answer indices after removal
      const selected = iq.correctAnswer.split(",").filter(Boolean);
      iq.correctAnswer = selected
        .filter((s) => s !== String(optIndex))
        .map((s) => (Number(s) > optIndex ? String(Number(s) - 1) : s))
        .join(",");
      setSections(updated);
    }
  };

  // Add a question ID to a specific section's questionIds
  const addQuestionToSection = (questionId: number, sectionIndex: number) => {
    const current = sections[sectionIndex].questionIds;
    const existingIds = parseIds(current);
    if (existingIds.includes(questionId)) return;
    const updated = current.trim()
      ? `${current.trim()}, ${questionId}`
      : String(questionId);
    updateSection(sectionIndex, "questionIds", updated);
  };

  // Convert InlineQuestion[] to API payload format
  const buildInlinePayload = (
    iqs: InlineQuestion[]
  ): InlineQuestionPayload[] => {
    return iqs
      .filter((iq) => iq.questionText.trim() !== "")
      .map((iq) => ({
        questionText: iq.questionText.trim(),
        questionType: iq.questionType,
        options: iq.options.map((text, i) => ({
          text: text.trim(),
          isCorrect: iq.correctAnswer.split(",").filter(Boolean).includes(String(i)),
        })),
        marks: iq.marks || 1,
        difficulty: iq.difficulty || "",
        concept: iq.concept || "",
      }));
  };

  // Convert form state to API payload (filters sections with content)
  const buildSectionsPayload = (): SectionInput[] => {
    return sections
      .filter(
        (s) =>
          s.name.trim() !== "" ||
          s.questionIds.trim() !== "" ||
          s.inlineQuestions.length > 0
      )
      .map((s) => ({
        name: s.name.trim(),
        questionIds: parseIds(s.questionIds),
        source: s.source,
        keywordId: s.keywordId ? Number(s.keywordId) : undefined,
        inlineQuestions: buildInlinePayload(s.inlineQuestions),
        subSections: s.subSections
          .filter(
            (sub) => sub.name.trim() !== "" || sub.questionIds.trim() !== ""
          )
          .map((sub) => ({
            name: sub.name.trim(),
            questionIds: parseIds(sub.questionIds),
            source: sub.source,
          })),
      }));
  };

  // Build raw sections payload (includes sections with names only, for unchecked questions checkbox)
  const buildRawSectionsPayload = (): SectionInput[] => {
    return sections
      .filter((s) => s.name.trim() !== "") // Only filter by name
      .map((s) => ({
        name: s.name.trim(),
        questionIds: [],  // Empty question IDs
        source: s.source,
        keywordId: s.keywordId ? Number(s.keywordId) : undefined,
        inlineQuestions: [],  // Empty inline questions
        subSections: s.subSections
          .filter((sub) => sub.name.trim() !== "")
          .map((sub) => ({
            name: sub.name.trim(),
            questionIds: [],  // Empty question IDs
            source: sub.source,
          })),
      }));
  };

  // Search questions
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const result = await QuickQuizService.searchQuestions(
        searchQuery,
        searchSource
      );
      setSearchResults(result.questions);
    } catch (err: any) {
      console.error("Search failed:", err);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, searchSource]);

  // Client-side validation for inline questions
  const validateInlineQuestions = (): string[] => {
    const errs: string[] = [];
    sections.forEach((s, si) => {
      s.inlineQuestions.forEach((iq, qi) => {
        const prefix = `Section ${si + 1}, New Q${qi + 1}`;
        if (!iq.questionText.trim()) {
          errs.push(`${prefix}: Question text is required`);
        }
        if (iq.marks <= 0) {
          errs.push(`${prefix}: Marks must be greater than 0`);
        }
        if (iq.questionType === "mcq" || iq.questionType === "tf") {
          const nonEmpty = iq.options.filter((o) => o.trim() !== "");
          if (nonEmpty.length < 2) {
            errs.push(`${prefix}: At least 2 options are required`);
          }
          if (iq.correctAnswer === "") {
            errs.push(`${prefix}: Please select the correct answer`);
          }
        }
      });
    });
    return errs;
  };

  // Check for duplicate question IDs within and across sections
  const findDuplicateQuestionIds = (): string[] => {
    const errs: string[] = [];
    const globalSeen = new Map<number, string>(); // id -> first location label
    sections.forEach((s, si) => {
      const sLabel = `Section ${si + 1}${s.name ? ` (${s.name})` : ""}`;
      const ids = parseIds(s.questionIds);
      // Duplicates within same section
      const localSeen = new Set<number>();
      for (const id of ids) {
        if (localSeen.has(id)) {
          errs.push(`Duplicate question ID ${id} in ${sLabel}`);
        } else {
          localSeen.add(id);
        }
        // Duplicates across sections
        if (globalSeen.has(id) && globalSeen.get(id) !== sLabel) {
          errs.push(`Question ID ${id} is duplicated in ${globalSeen.get(id)} and ${sLabel}`);
        } else if (!globalSeen.has(id)) {
          globalSeen.set(id, sLabel);
        }
      }
      // Check sub-sections
      s.subSections.forEach((sub, subIdx) => {
        const subLabel = `${sLabel} > Sub-Section ${subIdx + 1}`;
        const subIds = parseIds(sub.questionIds);
        const subLocalSeen = new Set<number>();
        for (const id of subIds) {
          if (subLocalSeen.has(id)) {
            errs.push(`Duplicate question ID ${id} in ${subLabel}`);
          } else {
            subLocalSeen.add(id);
          }
          if (globalSeen.has(id)) {
            errs.push(`Question ID ${id} is duplicated in ${globalSeen.get(id)} and ${subLabel}`);
          } else {
            globalSeen.set(id, subLabel);
          }
        }
      });
    });
    return errs;
  };

  // Validate
  const handleValidate = async () => {
    if (!hasQuestions) return;
    setValidating(true);
    setErrors([]);
    try {
      // Client-side inline question validation first
      const inlineErrors = validateInlineQuestions();
      if (inlineErrors.length > 0) {
        setErrors(inlineErrors);
        return;
      }

      const dupErrors = findDuplicateQuestionIds();
      if (dupErrors.length > 0) {
        setErrors(dupErrors);
        return;
      }

      const sectionsPayload = buildSectionsPayload();
      if (sectionsPayload.length === 0) {
        setErrors(["At least one section with questions is required"]);
        return;
      }
      const result = await QuickQuizService.validateSections(sectionsPayload);
      if (result.valid) {
        setErrors([]);
        const inlineCount = result.inlineQuestionCount || 0;
        const bankCount = (result.questions?.length || 0);
        alert(
          `Validation passed! ${bankCount} existing question(s) valid` +
            (inlineCount > 0
              ? `, ${inlineCount} new question(s) will be created.`
              : ".")
        );
      } else {
        setErrors(result.errors);
      }
    } catch (err: any) {
      setErrors([err.message || "Validation failed"]);
    } finally {
      setValidating(false);
    }
  };

  // Build payload from form state
  const buildPayload = (): CreateQuizPayload | null => {
    const errs: string[] = [];
    if (!testName.trim()) errs.push("Test name is required");
    if (!testDate) errs.push("Test date is required");
    if (!testTime) errs.push("Duration is required");
    if (!categoryId) errs.push("Test category is required");
    if (!testTypeId) errs.push("Test type is required");
    if (!playerId) errs.push("Player is required");
    if (lookups?.years && lookups.years.length > 0 && selectedYearIds.length === 0) errs.push("At least one year must be selected");
    if (errs.length > 0) {
      setErrors(errs);
      return null;
    }
    // Build sections payload based on checkbox state
    let sectionsPayload: SectionInput[] = [];

    if (hasQuestions) {
      // Questions checkbox is CHECKED - build sections with questions and validate

      // Validate inline questions client-side before submission
      const inlineErrors = validateInlineQuestions();
      if (inlineErrors.length > 0) {
        setErrors(inlineErrors);
        return null;
      }

      sectionsPayload = buildSectionsPayload();

      // Check if user has sections blocks but didn't fill any questions
      if (sectionsPayload.length === 0) {
        if (sections.length > 0) {
          // Sections blocks exist but no content filled
          errs.push("You have checked 'Add Questions to this Test' but haven't added any questions to the sections. Please add a section name with question IDs, or uncheck the checkbox.");
        } else {
          // No sections blocks added at all
          errs.push("Please add at least one section with questions, or uncheck 'Add Questions to this Test'.");
        }
        setErrors(errs);
        return null;
      }

      // Check for duplicate question IDs across all sections
      const dupErrors = findDuplicateQuestionIds();
      if (dupErrors.length > 0) {
        setErrors(dupErrors);
        return null;
      }
    } else {
      // Questions checkbox is UNCHECKED - send raw sections (with names only, for adding questions later)
      sectionsPayload = buildRawSectionsPayload();
    }

    return {
      name: testName.trim(),
      sections: sectionsPayload,
      categoryId: categoryId ? Number(categoryId) : undefined,
      testTypeId: testTypeId ? Number(testTypeId) : undefined,
      playerId: playerId ? Number(playerId) : undefined,
      testDate: testDate || undefined,
      testTime: testTime ? Number(testTime) : undefined,
      testResultTypeId: testResultTypeId ? Number(testResultTypeId) : 0, // Send 0 for "Default" (backend converts to NULL)
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      testCutOffTime: testCutOffTime || undefined,
      sortOrder: sortOrder ? Number(sortOrder) : undefined,
      choiceSequence: choiceSequence || undefined,
      password: testPassword || undefined,
      remarks: remarks || undefined,
      rankDefinition: rankDefinition || undefined,
      yearIds: selectedYearIds.length > 0 ? selectedYearIds : undefined,
      // Send the rich-text fields always so clearing them persists on edit.
      instructions: instructions || "",
      mobileInstructions: mobileInstructions || "",
      testDeclarationRemarks: testDeclarationRemarks || "",
      studentAnnouncementRemarks: studentAnnouncementRemarks || "",
    };
  };

  // Create quiz
  const handleCreate = async () => {
    setLoading(true);
    setErrors([]);
    try {
      const payload = buildPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      // If questions checkbox is checked, validate questions exist in DB before creating
      if (hasQuestions && payload.sections.length > 0) {
        const validationResult = await QuickQuizService.validateSections(payload.sections);
        if (!validationResult.valid) {
          setErrors(validationResult.errors);
          setLoading(false);
          return;
        }
      }

      const result = await QuickQuizService.createQuiz(payload);
      if (result.success) {
        if (onCreated) {
          onCreated({ testId: result.data.testId, name: payload.name });
        } else if (onSuccess) {
          onSuccess({
            testId: result.data.testId,
            totalQuestions: result.data.totalQuestions,
            totalMarks: result.data.totalMarks,
          });
        }
      } else {
        setErrors([(result as any).message || "Creation failed"]);
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.message || err.message || "Failed to create quiz";
      setErrors([msg]);
    } finally {
      setLoading(false);
    }
  };

  // Update quiz
  const handleUpdate = async () => {
    if (!editingTestId) return;
    setLoading(true);
    setErrors([]);
    try {
      const payload = buildPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      // If questions checkbox is checked, validate questions exist in DB before updating
      if (hasQuestions && payload.sections.length > 0) {
        const validationResult = await QuickQuizService.validateSections(payload.sections);
        if (!validationResult.valid) {
          setErrors(validationResult.errors);
          setLoading(false);
          return;
        }
      }

      const result = await QuickQuizService.updateQuiz(editingTestId, payload);
      if (result.success) {
        onSuccess({
          testId: editingTestId,
          totalQuestions: result.data.totalQuestions,
          totalMarks: result.data.totalMarks,
        });
      } else {
        setErrors([(result as any).message || "Update failed"]);
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.message || err.message || "Failed to update quiz";
      setErrors([msg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Test Metadata */}
      <Card className="mb-3" style={{ border: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderRadius: 10, overflow: 'hidden' }}>
        <Card.Body style={{ padding: 0 }}>
          {/* Section 1: Basic Info */}
          <div style={{ padding: '18px 22px 14px' }}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Test Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Enter test name" value={testName}
                    onChange={(e) => setTestName(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Test Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="date" value={testDate} onChange={(e) => setTestDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Duration (mins) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="number" placeholder="e.g. 60" value={testTime}
                    onChange={(e) => setTestTime(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    <option value="">-- Select Category --</option>
                    {lookups?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Test Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select value={testTypeId} onChange={(e) => setTestTypeId(e.target.value)}>
                    <option value="">-- Select Type --</option>
                    {lookups?.testTypes.map((t) => <option key={t.id} value={t.id}>{t.displayLabel || t.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Player <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select value={playerId} onChange={(e) => setPlayerId(e.target.value)}>
                    <option value="">-- Select Player --</option>
                    {lookups?.players.map((p) => <option key={p.id} value={p.id}>{p.displayName || p.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                    Result Type
                  </Form.Label>
                  <Form.Select value={testResultTypeId} onChange={(e) => setTestResultTypeId(e.target.value)}>
                    <option value="">-- Default --</option>
                    <option value="1">Instant Result</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {lookups?.years && lookups.years.length > 0 && (
              <Row className="g-3 mt-1">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>
                      Year(s) <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0' }}>
                      {lookups.years.map((y) => (
                        <label key={y.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                          padding: '4px 12px', borderRadius: 16, fontSize: '0.8rem', fontWeight: 500,
                          border: selectedYearIds.includes(y.id) ? '2px solid #667eea' : '1px solid #ddd',
                          background: selectedYearIds.includes(y.id) ? '#eef2ff' : '#fff',
                          color: selectedYearIds.includes(y.id) ? '#4338ca' : '#555',
                          transition: 'all 0.15s',
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedYearIds.includes(y.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedYearIds([...selectedYearIds, y.id]);
                              else setSelectedYearIds(selectedYearIds.filter(id => id !== y.id));
                            }}
                            style={{ display: 'none' }}
                          />
                          {selectedYearIds.includes(y.id) && <span style={{ color: 'var(--brand-primary)' }}>&#10003;</span>}
                          {y.name}
                        </label>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '0 22px' }} />

          {/* Section 2: Schedule & Timing */}
          <div style={{ padding: '14px 22px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Schedule & Timing
            </div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Start Date & Time</Form.Label>
                  <Form.Control type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>End Date & Time</Form.Label>
                  <Form.Control type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Test CutOff Time</Form.Label>
                  <Form.Control type="datetime-local" value={testCutOffTime} onChange={(e) => setTestCutOffTime(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '0 22px' }} />

          {/* Section 3: Advanced Settings */}
          <div style={{ padding: '14px 22px 18px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Advanced Settings
            </div>
            <Row className="g-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Sort Order</Form.Label>
                  <Form.Control type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Choice Sequence</Form.Label>
                  <Form.Control type="text" value={choiceSequence} onChange={(e) => setChoiceSequence(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Test Password</Form.Label>
                  <Form.Control type="text" value={testPassword} onChange={(e) => setTestPassword(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Remarks</Form.Label>
                  <Form.Control as="textarea" rows={1} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <div style={{ marginTop: 12 }}>
              <Form.Check
                type="switch"
                label={<span style={{ fontSize: '0.82rem', fontWeight: 500 }}>Rank Definition Required</span>}
                checked={rankDefinition}
                onChange={(e) => setRankDefinition(e.target.checked)}
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Instructions & Messages (rich text) */}
      <Card className="mb-3" style={{ border: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderRadius: 10, overflow: 'hidden' }}>
        <Card.Body style={{ padding: '18px 22px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
            Instructions & Messages
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Instructions</Form.Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={instructions}
                  config={{ placeholder: "Enter web instructions shown to students..." }}
                  onChange={(_e: any, editor: any) => setInstructions(editor.getData())}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Mobile Instructions</Form.Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={mobileInstructions}
                  config={{ placeholder: "Enter instructions shown on the mobile app..." }}
                  onChange={(_e: any, editor: any) => setMobileInstructions(editor.getData())}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Test Results Declaration Message</Form.Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={testDeclarationRemarks}
                  config={{ placeholder: "Message shown when results are declared..." }}
                  onChange={(_e: any, editor: any) => setTestDeclarationRemarks(editor.getData())}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-body)' }}>Student Announcement Remarks</Form.Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={studentAnnouncementRemarks}
                  config={{ placeholder: "Announcement remarks shown to students..." }}
                  onChange={(_e: any, editor: any) => setStudentAnnouncementRemarks(editor.getData())}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Toggle: has questions? */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
        padding: "10px 16px", background: "var(--surface-page)", borderRadius: 8, border: "1px solid var(--border-default)",
      }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: "0.88rem", fontWeight: 600, color: "var(--text-strong)" }}>
          <input
            type="checkbox"
            checked={hasQuestions}
            onChange={(e) => setHasQuestions(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: "var(--brand-primary)", cursor: "pointer" }}
          />
          Add Questions to this Test
        </label>
        {!hasQuestions && (
          <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
            Test will be created without questions. You can add them later.
          </span>
        )}
      </div>

      {/* Question Search Panel */}
      {hasQuestions && <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Search Questions</span>
          <div className="d-flex gap-2 align-items-center">
            <Form.Select
              size="sm"
              style={{ width: "220px" }}
              value={targetSectionIndex}
              onChange={(e) => setTargetSectionIndex(Number(e.target.value))}
            >
              {sections.map((s, i) => (
                <option key={i} value={i}>
                  Add to: Section {i + 1}{s.name ? ` - ${s.name}` : ""}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size="sm"
              style={{ width: "180px" }}
              value={searchSource}
              onChange={(e) => setSearchSource(e.target.value as any)}
            >
              <option value="both">Both Sources</option>
              <option value="question_bank">Question Bank</option>
              <option value="test_section">Existing Tests</option>
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search by ID, name, or question text..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={searching}
            >
              {searching ? (
                <Spinner role="status" animation="border" size="sm" />
              ) : (
                "Search"
              )}
            </Button>
          </InputGroup>

          {searchResults.length > 0 && (
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                fontSize: "0.85rem",
              }}
            >
              <table className="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Source</th>
                    <th>Question</th>
                    <th>Marks</th>
                    <th>Topic/Concept</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((q) => {
                    const targetIds = parseIds(sections[targetSectionIndex]?.questionIds || "");
                    const alreadyAdded = targetIds.includes(q.id);
                    return (
                      <tr key={`${q.source}-${q.id}`}>
                        <td>{q.id}</td>
                        <td>
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: q.source === "question_bank" ? "#0d6efd" : "#198754",
                            }}
                          >
                            {q.source === "question_bank" ? "QB" : "Test"}
                          </span>
                        </td>
                        <td
                          title={q.stemStatement || q.name}
                          style={{
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {q.stemStatement || q.name || "-"}
                        </td>
                        <td>{q.mark || "-"}</td>
                        <td>{q.topicName || q.concept || "-"}</td>
                        <td>
                          {alreadyAdded ? (
                            <span style={{ color: "var(--status-success)", fontSize: "0.8rem" }}>
                              Added
                            </span>
                          ) : (
                            <Button
                              variant="primary"
                              size="sm"
                              style={{ padding: "0 8px", fontSize: "0.8rem", lineHeight: "1.4" }}
                              onClick={() => addQuestionToSection(q.id, targetSectionIndex)}
                            >
                              + Add
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>}

      {/* Sections */}
      {sections.map((section, sIndex) => (
        <Card key={sIndex} className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>
              Section {sIndex + 1}
              {section.name && (
                <span className="text-muted"> - {section.name}</span>
              )}
              {(parseIds(section.questionIds).length > 0 ||
                section.inlineQuestions.length > 0) && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    marginLeft: "8px",
                  }}
                >
                  ({parseIds(section.questionIds).length +
                    section.inlineQuestions.length}{" "}
                  questions)
                </span>
              )}
            </span>
            <div>
              {hasQuestions && section.subSections.length < MAX_SUB_SECTIONS && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="me-2"
                  onClick={() => addSubSection(sIndex)}
                >
                  + Sub-Section
                </Button>
              )}
              <button
                type="button"
                title={sections.length <= 1 ? "Clear section" : "Remove section"}
                onClick={() => removeSection(sIndex)}
                style={{
                  width: 28, height: 28, borderRadius: "50%", border: "none",
                  background: "var(--status-danger-bg)", color: "var(--status-danger)", fontSize: "1.1rem",
                  fontWeight: 700, lineHeight: 1, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s", marginLeft: 4,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--status-danger)"; e.currentTarget.style.color = "var(--text-on-brand)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "var(--status-danger-bg)"; e.currentTarget.style.color = "var(--status-danger)"; }}
              >
                &times;
              </button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Section Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Physics"
                    value={section.name}
                    onChange={(e) =>
                      updateSection(sIndex, "name", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Source</Form.Label>
                  <Form.Select
                    value={section.source}
                    onChange={(e) =>
                      updateSection(sIndex, "source", e.target.value)
                    }
                  >
                    <option value="question_bank">Question Bank</option>
                    <option value="test_section">Existing Tests</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Keyword</Form.Label>
                  <Form.Select
                    value={section.keywordId}
                    onChange={(e) =>
                      updateSection(sIndex, "keywordId", e.target.value)
                    }
                  >
                    <option value="">-- None --</option>
                    {lookups?.keywords.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {hasQuestions && (
              <Col md={5}>
                <Form.Group>
                  <Form.Label>
                    Question IDs{" "}
                    <span className="text-muted">(comma-separated)</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g. 101, 102, 103, 104"
                    value={section.questionIds}
                    onChange={(e) =>
                      updateSection(sIndex, "questionIds", e.target.value)
                    }
                  />
                  {(section.questionIds || section.inlineQuestions.length > 0) && (
                    <Form.Text className="text-muted">
                      {parseIds(section.questionIds).length} from bank
                      {section.inlineQuestions.length > 0 &&
                        `, ${section.inlineQuestions.length} new`}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              )}
            </Row>

            {/* + New Question button (hidden when editing an existing test) */}
            {hasQuestions && !editingTestId && (
            <div className="mb-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => addInlineQuestion(sIndex)}
              >
                + New Question
              </Button>
            </div>
            )}

            {/* Inline question forms */}
            {section.inlineQuestions.map((iq, iqIndex) => (
              <Card
                key={iq.tempId}
                className="mb-2"
                style={{ border: "1px solid var(--border-default)" }}
              >
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="fw-bold">
                      New Question {iqIndex + 1}
                    </small>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeInlineQuestion(sIndex, iqIndex)}
                    >
                      &times;
                    </Button>
                  </div>

                  {/* Type, Marks, Difficulty row */}
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Select
                        size="sm"
                        value={iq.questionType}
                        onChange={(e) =>
                          updateInlineQuestion(sIndex, iqIndex, {
                            questionType: e.target.value as any,
                          })
                        }
                      >
                        <option value="mcq">MCQ</option>
                        <option value="tf">True / False</option>
                        <option value="descriptive">Descriptive</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={0}
                        step={0.5}
                        placeholder="Marks"
                        value={iq.marks}
                        onChange={(e) =>
                          updateInlineQuestion(sIndex, iqIndex, {
                            marks: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        size="sm"
                        value={iq.difficulty}
                        onChange={(e) =>
                          updateInlineQuestion(sIndex, iqIndex, {
                            difficulty: e.target.value,
                          })
                        }
                      >
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Concept (optional)"
                        value={iq.concept}
                        onChange={(e) =>
                          updateInlineQuestion(sIndex, iqIndex, {
                            concept: e.target.value,
                          })
                        }
                      />
                    </Col>
                  </Row>

                  {/* Question text */}
                  <Form.Control
                    size="sm"
                    as="textarea"
                    rows={2}
                    placeholder="Question text..."
                    className="mb-2"
                    value={iq.questionText}
                    onChange={(e) =>
                      updateInlineQuestion(sIndex, iqIndex, {
                        questionText: e.target.value,
                      })
                    }
                  />

                  {/* Options for MCQ / TF */}
                  {(iq.questionType === "mcq" || iq.questionType === "tf") && (
                    <div className="mb-2">
                      {iq.options.map((opt, optIdx) => (
                        <div
                          key={optIdx}
                          className="d-flex align-items-center gap-2 mb-1"
                        >
                          <Form.Check
                            type={iq.questionType === "mcq" ? "checkbox" : "radio"}
                            name={`correct-${iq.tempId}`}
                            checked={
                              iq.questionType === "mcq"
                                ? iq.correctAnswer.split(",").filter(Boolean).includes(String(optIdx))
                                : iq.correctAnswer === String(optIdx)
                            }
                            onChange={() => {
                              if (iq.questionType === "mcq") {
                                const selected = iq.correctAnswer.split(",").filter(Boolean);
                                const idx = String(optIdx);
                                const updated = selected.includes(idx)
                                  ? selected.filter((s) => s !== idx)
                                  : [...selected, idx];
                                updateInlineQuestion(sIndex, iqIndex, {
                                  correctAnswer: updated.join(","),
                                });
                              } else {
                                updateInlineQuestion(sIndex, iqIndex, {
                                  correctAnswer: String(optIdx),
                                });
                              }
                            }}
                            title="Mark as correct answer"
                          />
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder={`Option ${optIdx + 1}`}
                            value={opt}
                            onChange={(e) =>
                              updateInlineOption(
                                sIndex,
                                iqIndex,
                                optIdx,
                                e.target.value
                              )
                            }
                            disabled={iq.questionType === "tf"}
                            style={{ flex: 1 }}
                          />
                          {iq.questionType === "mcq" &&
                            iq.options.length > 2 && (
                              <Button
                                variant="danger"
                                size="sm"
                                style={{
                                  padding: "0 6px",
                                  fontSize: "0.75rem",
                                  lineHeight: "1.4",
                                }}
                                onClick={() =>
                                  removeInlineOption(sIndex, iqIndex, optIdx)
                                }
                              >
                                &times;
                              </Button>
                            )}
                        </div>
                      ))}
                      {iq.questionType === "mcq" && iq.options.length < 5 && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          onClick={() => addInlineOption(sIndex, iqIndex)}
                        >
                          + Add Option
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Answer for descriptive */}
                  {iq.questionType === "descriptive" && (
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      placeholder="Answer text..."
                      className="mb-2"
                      value={iq.correctAnswer}
                      onChange={(e) =>
                        updateInlineQuestion(sIndex, iqIndex, {
                          correctAnswer: e.target.value,
                        })
                      }
                    />
                  )}
                </Card.Body>
              </Card>
            ))}

            {/* Sub-sections */}
            {hasQuestions && section.subSections.map((sub, subIndex) => (
              <Card
                key={subIndex}
                className="mb-2"
                style={{ backgroundColor: "var(--surface-page)" }}
              >
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted fw-bold">
                      Sub-Section {subIndex + 1}
                    </small>
                    <button
                      type="button"
                      title="Remove sub-section"
                      onClick={() => removeSubSection(sIndex, subIndex)}
                      style={{
                        width: 24, height: 24, borderRadius: "50%", border: "none",
                        background: "var(--status-danger-bg)", color: "var(--status-danger)", fontSize: "1rem",
                        fontWeight: 700, lineHeight: 1, cursor: "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--status-danger)"; e.currentTarget.style.color = "var(--text-on-brand)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "var(--status-danger-bg)"; e.currentTarget.style.color = "var(--status-danger)"; }}
                    >
                      &times;
                    </button>
                  </div>
                  <Row>
                    <Col md={3}>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Sub-section name"
                        value={sub.name}
                        onChange={(e) =>
                          updateSubSection(
                            sIndex,
                            subIndex,
                            "name",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Select
                        size="sm"
                        value={sub.source}
                        onChange={(e) =>
                          updateSubSection(
                            sIndex,
                            subIndex,
                            "source",
                            e.target.value
                          )
                        }
                      >
                        <option value="question_bank">QB</option>
                        <option value="test_section">Test</option>
                      </Form.Select>
                    </Col>
                    <Col md={7}>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={1}
                        placeholder="Question IDs (comma-separated)"
                        value={sub.questionIds}
                        onChange={(e) =>
                          updateSubSection(
                            sIndex,
                            subIndex,
                            "questionIds",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </Card.Body>
        </Card>
      ))}

      {/* Add Section Button */}
      {sections.length < MAX_SECTIONS && (
        <div className="mb-3">
          <Button variant="secondary" onClick={addSection}>
            + Add Section
          </Button>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <Alert variant="danger" dismissible onClose={() => setErrors([])}>
          <strong>Errors:</strong>
          <ul className="mb-0 mt-1">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Action Buttons */}
      <Card>
        <Card.Body className="d-flex gap-2">
          {hasQuestions && (
            <Button
              variant="secondary"
              onClick={handleValidate}
              disabled={validating || loading}
            >
              {validating ? (
                <>
                  <Spinner role="status" animation="border" size="sm" className="me-1" />
                  Validating...
                </>
              ) : (
                "Validate"
              )}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={editingTestId ? handleUpdate : handleCreate}
            disabled={loading || !testName.trim()}
          >
            {loading ? (
              <>
                <Spinner role="status" animation="border" size="sm" className="me-1" />
                {editingTestId ? "Updating..." : "Creating..."}
              </>
            ) : editingTestId ? (
              "Update Quiz"
            ) : (
              "Create Quiz"
            )}
          </Button>
          {editingTestId && onCancelEdit && (
            <Button variant="secondary" onClick={onCancelEdit}>
              Cancel Edit
            </Button>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default QuizFormPanel;
